import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AuthService from "common/services/users/AuthService";
import UsersService from "common/services/users/UsersService";
import MqttService from "common/services/mqtt/MqttService";
import { clearUser } from "redux/slices/user";
import { clearMQTTState } from "redux/slices/mqtt";
import useToastNotification from "hooks/useToastNotification";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Slide from "@mui/material/Slide";

function DeleteAccount() {
  const { t } = useTranslation();
  const showToast = useToastNotification();
  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const eventsSubscriptions = useSelector((state) => state.mqtt.eventsSubs);
  const telemetrySubscriptions = useSelector((state) => state.mqtt.telemetrySubs);
  const username = useSelector(state => state.user.username);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(true);

  const resetState = () => {
    setPassword("");
    setIsPasswordValid(true);
  };

  const onAccountDelete = async () => {
    try {
      await UsersService.deleteByUsername(username);
      await AuthService.deleteAccount();
      showToast("success", "Account eliminato con successo");
      handleLogout();
    } catch (error) {
      console.log("ERROR: ", error)
      showToast("error", "Errore nell'eliminazione dell'account");
    }
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      reduxDispatch(clearUser());

      if (eventsSubscriptions) {
        eventsSubscriptions.forEach((sub) => {
          MqttService.unsubscribe(sub);
        });
      }
      if (telemetrySubscriptions.length) {
        telemetrySubscriptions.forEach((sub) => {
          MqttService.unsubscribe(sub);
        });
      }
      reduxDispatch(clearMQTTState());
      MqttService.closeProvider();

      navigate("/login");
    } catch (err) {
      console.log(err);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await AuthService.verifyPassword(password);
      if (response.success) {
        setDialogOpen(false);
        onAccountDelete();
      } else {
        setIsPasswordValid(false);
      }
    } catch (error) {
      setIsPasswordValid(false);
    }
  };

  const handleOpenDialog = () => {
    resetState(); // Resetta lo stato prima di aprire il dialogo
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    resetState(); // Resetta lo stato quando si chiude il dialogo
    setDialogOpen(false);
  };

  return (
    <Card>
      <MDBox
        pr={3}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <MDBox p={3} lineHeight={1}>
          <MDBox mb={1}>
            <MDTypography variant="h5">{t("deleteAccount.componentTitle")}</MDTypography>
          </MDBox>
          <MDTypography variant="button" color="text">
            {t("deleteAccount.componentContext")}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" flexDirection="row" alignItems="center">
          <MDButton variant="gradient" color="primary" onClick={handleOpenDialog}>
            {t("deleteAccount.buttonDeleteAccount")}
          </MDButton>
        </MDBox>
      </MDBox>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="dialog-title"
        TransitionComponent={Slide}
        transitionDuration={500}
        transitionDirection="up"
      >
        <DialogTitle id="dialog-title">{t("deleteAccount.modalTitle")}</DialogTitle>
        <DialogContent>
          <MDBox mb={3} mt={1} pl={1} pr={1}>
            <MDTypography variant="subtitle2" color="text">
              {t("deleteAccount.modalContext")}
            </MDTypography>
          </MDBox>
          <MDBox pl={1} pr={1}>
            <TextField
              fullWidth
              type="password"
              label={t("deleteAccount.modalInputPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!isPasswordValid}
              helperText={!isPasswordValid && t("deleteAccount.modalInputErrorMessage")}
              variant="outlined"
              InputProps={{
                sx: {
                  "& .MuiInputBase-input": {
                    transition: "border-color 0.3s ease-in-out",
                  },
                  "& .Mui-error .MuiInputBase-input": {
                    borderColor: "#f44336",
                  },
                },
              }}
              FormHelperTextProps={{
                sx: { color: "red" },
              }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDBox display="flex" justifyContent="flex-end" width="100%">
            <MDButton onClick={handleCloseDialog}>
              {t("buttonCancel")}
            </MDButton>
            <MDButton type="submit" onClick={handleConfirmDelete}>
              {t("buttonConfirm")}
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default DeleteAccount;
